import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  CreateHomeworkAssignmentRequestBody,
  GetTopicsCourseType,
} from "@packages/api/madlenAPI.schemas";
import CommonPropertiesStep from "./CommonPropertiesStep";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import { Button, Text } from "../../../../../core";
import { Input } from "@core/input";
import CourseSelector from "../course-selector";
import GradeSelector from "../../../../tools/components/grade-selector";
import { useAppSelector } from "@packages/store/hooks";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";

export type CurriculumType = "MEB" | "INTERNATIONAL";
export type InternationalCurriculum = "AP" | "IGCSE" | "IB" | "ALEVEL";

interface YoutubeStepProps {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  onChange: (data: Partial<CreateHomeworkAssignmentRequestBody>) => void;
}

const YoutubeStep: React.FC<YoutubeStepProps> = ({
  assignmentData,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState<string>(assignmentData.prompt || "");
  const [youtubeLink, setYoutubeLink] = useState<string>(
    assignmentData.youtubeLink || "",
  );
  const [selectedGrade, setSelectedGrade] = useState<number>(
    assignmentData.grade || 8,
  );
  const getInitialCurriculum = () =>
    i18n.language === "tr" ? "MEB" : "INTERNATIONAL";
  const getInitialInternationalCurriculum = () => "AP";
  const getInitialCourse = (curriculum: CurriculumType) => {
    if (curriculum === "MEB") {
      return "HISTORY";
    } else {
      return "AP_2025_WORLD_HISTORY_MODERN";
    }
  };
  const [curriculumType, setCurriculumType] = useState<CurriculumType>(
    getInitialCurriculum(),
  );
  const [internationalCurriculum, setInternationalCurriculum] =
    useState<InternationalCurriculum>(getInitialInternationalCurriculum());
  const [selectedCourse, setSelectedCourse] = useState<GetTopicsCourseType>(
    assignmentData.courseType || getInitialCourse(curriculumType),
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    assignmentData.contentLanguage || i18n.language,
  );
  const userEmail = useAppSelector((state) => state.user.teacher?.email);
  const shouldShowMathematics =
    userEmail === "batuhan@madlen.io" || userEmail === "kadriyezobu@gmail.com";

  useEffect(() => {
    onChange({ generator: "YOUTUBE_LINK" });
  }, []);

  // Update curriculum type and course when language changes
  useEffect(() => {
    const newCurriculumType = getInitialCurriculum();
    setCurriculumType(newCurriculumType);
    setInternationalCurriculum(getInitialInternationalCurriculum());
    const newCourse = getInitialCourse(newCurriculumType);
    setSelectedCourse(newCourse);
    onChange({
      courseType: newCourse,
    });
  }, [i18n.language]);

  const handleTopicChange = (newTopic: string) => {
    setTopic(newTopic);
    onChange({ prompt: newTopic });
  };

  const getFilteredCourses = (): GetTopicsCourseType[] => {
    if (curriculumType === "MEB") {
      return Object.keys(CourseData).filter(
        (key) =>
          isTurkishCourse(key) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    } else {
      return Object.keys(CourseData).filter(
        (key) =>
          key.startsWith(`${internationalCurriculum}_`) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg mb-6">
        <Text variant="p" status="info" size="sm">
          {t("assignments.generator.youtube.title")}
        </Text>
        <Text variant="p" status="info" size="sm">
          {t("assignments.generator.youtube.description")}
        </Text>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CommonPropertiesStep
          assignmentData={assignmentData}
          onChange={onChange}
        />
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            <GradeSelector
              selectedGrade={selectedGrade}
              minGrade={1}
              setSelectedGrade={(grade) => {
                const availableCourses = Object.entries(CourseData).filter(
                  ([, value]) => value.visible(grade),
                );
                const firstAvailableCourse =
                  availableCourses[0]?.[0] as GetTopicsCourseType;
                const doesPreviousCourseExist = availableCourses.some(
                  ([key]) => key === selectedCourse,
                );
                setSelectedGrade(grade);
                if (!doesPreviousCourseExist) {
                  setSelectedCourse(firstAvailableCourse);
                }
                onChange({
                  grade,
                  ...(!doesPreviousCourseExist && {
                    courseType: firstAvailableCourse,
                  }),
                  topicIds: [],
                });
              }}
            />
          </div>
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("hooks.form.outputLanguage")}
          </h4>
          <OutputLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={(language) => {
              setSelectedLanguage(language);
              onChange({ contentLanguage: language });
            }}
          />
        </div>
      </div>

      <div className="space-y-4">
        {/* Curriculum Type Selection */}
        <div className="space-y-2">
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("common.curriculumType")}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              onClick={() => {
                setCurriculumType("MEB");
                const mebCourses = Object.keys(CourseData).filter(
                  (key) =>
                    isTurkishCourse(key) &&
                    CourseData[key as GetTopicsCourseType].visible(
                      selectedGrade,
                    ),
                ) as GetTopicsCourseType[];
                if (mebCourses.length > 0) {
                  setSelectedCourse(mebCourses[0]);
                  onChange({
                    courseType: mebCourses[0],
                  });
                }
              }}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "MEB"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "MEB"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.nationalCurriculum")}
              </span>
            </Button>
            <Button
              type="button"
              onClick={() => {
                setCurriculumType("INTERNATIONAL");
                const intlCourses = Object.keys(CourseData).filter(
                  (key) =>
                    key.startsWith(`${internationalCurriculum}_`) &&
                    CourseData[key as GetTopicsCourseType].visible(
                      selectedGrade,
                    ),
                ) as GetTopicsCourseType[];
                if (intlCourses.length > 0) {
                  setSelectedCourse(intlCourses[0]);
                  onChange({
                    courseType: intlCourses[0],
                  });
                }
              }}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "INTERNATIONAL"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "INTERNATIONAL"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.internationalCurriculum")}
              </span>
            </Button>
          </div>
        </div>

        {/* International Curriculum Sub-selection */}
        {curriculumType === "INTERNATIONAL" && (
          <div className="space-y-2">
            <h4
              className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
            >
              {t("common.selectInternationalProgram")}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                ["AP", "IGCSE", "IB", "ALEVEL"] as InternationalCurriculum[]
              ).map((curriculum) => (
                <Button
                  key={curriculum}
                  type="button"
                  onClick={() => {
                    setInternationalCurriculum(curriculum);
                    const intlCourses = Object.keys(CourseData).filter(
                      (key) =>
                        key.startsWith(`${curriculum}_`) &&
                        CourseData[key as GetTopicsCourseType].visible(
                          selectedGrade,
                        ),
                    ) as GetTopicsCourseType[];
                    if (intlCourses.length > 0) {
                      setSelectedCourse(intlCourses[0]);
                      onChange({
                        courseType: intlCourses[0],
                      });
                    }
                  }}
                  variant="solid"
                  size="md"
                  rounded="lg"
                  textColor={
                    internationalCurriculum === curriculum
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }
                  bgColor={
                    internationalCurriculum === curriculum
                      ? `bg-indigo-600 hover:bg-indigo-700`
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }
                  fullWidth
                >
                  <span className="text-sm font-medium">{curriculum}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1">
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CourseSelector
              selectedGrade={selectedGrade}
              filteredCourses={Object.values(GetTopicsCourseType)
                .filter((course) => !getFilteredCourses().includes(course))
                .concat(
                  shouldShowMathematics
                    ? []
                    : (["MATHEMATICS"] as GetTopicsCourseType[]),
                )}
              selectedCourse={selectedCourse}
              setSelectedCourse={(course) => {
                setSelectedCourse(course);
                onChange({
                  grade: selectedGrade,
                  courseType: course,
                });
              }}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.youtube.videoLink")}
          </label>
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col gap-6">
              {/* First row - YouTube input */}
              <div className="flex">
                <Input
                  data-testid="create-assignment-from-youtube-link-input"
                  type="text"
                  value={youtubeLink}
                  onChange={(e) => {
                    setYoutubeLink(e.target.value);
                    onChange({ youtubeLink: e.target.value });
                  }}
                  classNames={{
                    input:
                      "w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100",
                  }}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>

              {/* Second row - Radio options */}
              <div className="flex flex-col md:flex-row gap-4">
                <label
                  className={`flex-1 p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                    !topic
                      ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/50"
                      : "border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-400"
                  }`}
                >
                  <Input
                    type="radio"
                    classNames={{
                      input: "peer sr-only",
                    }}
                    name="videoScope"
                    checked={!topic}
                    onChange={() => {
                      setTopic("");
                      onChange({ prompt: "" });
                    }}
                  />
                  <div className="space-y-1">
                    <Text
                      variant="span"
                      className="font-medium block text-gray-700 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                    >
                      {t("assignments.generator.youtube.allVideo")}
                    </Text>
                    <Text
                      variant="p"
                      className="text-sm text-gray-500 dark:text-gray-400"
                    >
                      {t("assignments.generator.youtube.allVideoDesc")}
                    </Text>
                  </div>
                </label>

                <label
                  className={`flex-1 p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                    topic
                      ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/50"
                      : "border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-400"
                  }`}
                >
                  <Input
                    type="radio"
                    classNames={{
                      input: "peer sr-only",
                    }}
                    name="videoScope"
                    checked={Boolean(topic)}
                    onChange={() => {
                      setTopic(" ");
                      onChange({ prompt: " " });
                    }}
                  />
                  <div className="space-y-1">
                    <Text
                      variant="span"
                      className="font-medium block text-gray-700 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                    >
                      {t("assignments.generator.youtube.specificTopic")}
                    </Text>
                    <Text
                      variant="p"
                      className="text-sm text-gray-500 dark:text-gray-400"
                    >
                      {t("assignments.generator.youtube.specificTopicDesc")}
                    </Text>
                  </div>
                </label>
              </div>
            </div>

            {Boolean(topic) && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  {t("assignments.generator.youtube.examTopic")}
                </label>
                <div className="relative">
                  <textarea
                    value={topic}
                    onChange={(e) => handleTopicChange(e.target.value)}
                    className="w-full h-24 p-4 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder={t(
                      "assignments.generator.youtube.examTopicPlaceholder",
                    )}
                  />
                  <div className="absolute right-3 bottom-3 text-xs text-gray-400">
                    {topic.length}/200
                  </div>
                </div>
                <Text
                  variant="p"
                  className="mt-2 text-sm text-gray-500 dark:text-gray-400"
                >
                  {t("assignments.generator.youtube.examTopicDesc")}
                </Text>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.youtube.videoPreview")}
          </label>
          <div className="aspect-video w-full bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
            {youtubeLink ? (
              <iframe
                src={`https://www.youtube.com/embed/${
                  youtubeLink.includes("youtu.be")
                    ? youtubeLink.split("youtu.be/")[1]?.split("?")[0]
                    : youtubeLink.includes("/embed/")
                      ? youtubeLink.split("/embed/")[1]?.split("?")[0]
                      : youtubeLink.split("v=")[1]?.split("&")[0]
                }`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                <Text variant="p">
                  {t("assignments.generator.youtube.videoPreview")}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoutubeStep;
